<template>
    <div v-if="!props.hidden" 
        class=" w-full">
        <div class="p-4">
            <div class="flex items-center justify-between mb-4">
                <h3 class="text-sm font-medium text-gray-900 dark:text-white" v-if="props.showLabel">Auto Scroll Control</h3>
                <button 
                    @click="toggleScrolling"
                    class="flex items-center px-3 py-1 text-xs font-medium rounded-md transition-colors"
                    :class="isScrolling 
                        ? 'bg-red-100 text-red-700 hover:bg-red-200 dark:bg-red-900 dark:text-red-300 dark:hover:bg-red-800' 
                        : 'bg-green-100 text-green-700 hover:bg-green-200 dark:bg-green-900 dark:text-green-300 dark:hover:bg-green-800'"
                >
                    <svg v-if="isScrolling" class="w-3 h-3 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12"></path>
                    </svg>
                    <svg v-else class="w-3 h-3 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M5 13l4 4L19 7"></path>
                    </svg>
                    {{ isScrolling ? 'Stop' : 'Start' }}
                </button>
            </div>
            
            <div class="space-y-4">
                <div>
                    <!-- Volume Bar Visualization -->
                    <div class="flex items-end space-x-1 h-8 mb-2">
                        <div 
                            v-for="(bar, index) in 5" 
                            :key="index"
                            @click="selectSpeedByBar(index)"
                            class="flex-1 rounded-sm transition-all duration-200 cursor-pointer hover:opacity-80"
                            :class="getBarClass(index)"
                            :style="{ height: `${(index + 1) * 4}px` }"
                        ></div>
                    </div>
                    <div class="flex items-center space-x-2 w-full justify-between hidden md:flex">
                        <div @click="decreaseSpeed"
                                :disabled="scrollSpeed <= 1" 
                            class="cursor-pointer flex justify-center items-center rounded-full bg-white dark:bg-gray-500 p-2 border border-gray-300 dark:border-gray-600 hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors duration-200 transition-all hover:bg-grad  disabled:opacity-50 disabled:cursor-not-allowed">
                            <svg class="w-3 h-3 text-indigo-700 dark:text-white" aria-hidden="true" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 18 2">
                                <path stroke="currentColor" stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M1 1h16"/>
                            </svg>
                        </div>
                        <input 
                            v-model.number="scrollSpeed"
                            type="range"
                            min="1"
                            max="10"
                            step="0.5"
                            class="h-2 max-w-[150px] bg-gray-200 rounded-lg appearance-none cursor-pointer slider dark:bg-gray-600"
                        />
                        <div  @click="increaseSpeed"
                                :disabled="scrollSpeed >= 10" 
                        class="cursor-pointer flex justify-center items-center rounded-full bg-white dark:bg-gray-500 p-2 border border-gray-300 dark:border-gray-600 hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors duration-200 transition-all hover:bg-grad  ">
                            <svg class="w-3 h-3 text-indigo-700 dark:text-white" aria-hidden="true" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 18 18">
                                <path stroke="currentColor" stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 1v16M1 9h16"/>
                            </svg>
                        </div>
                    </div>
                </div>
                
                                    <div class="flex items-center justify-between text-xs text-gray-500 dark:text-gray-400">
                <span>Slowest</span>
                <span>Fastest</span>
            </div>
            
            <div class="mt-3 pt-3 border-t border-gray-200 dark:border-gray-600 hidden md:block" v-if="props.showKeyboardShortcuts">
                <p class="text-xs text-gray-500 dark:text-gray-400 mb-2">Keyboard shortcuts:</p>
                <div class="grid grid-cols-1 gap-1.5 text-xs text-gray-600 dark:text-gray-300">
                    <div><kbd class="px-1.5 py-0.5 bg-gray-100 dark:bg-gray-600 rounded font-mono text-[11px]">Page Down</kbd> Start / Speed up</div>
                    <div><kbd class="px-1.5 py-0.5 bg-gray-100 dark:bg-gray-600 rounded font-mono text-[11px]">Page Up</kbd> Slow down / Stop</div>
                </div>
            </div>
            </div>
        </div>
    </div>
</template>
<script setup>
import { ref, defineProps, defineEmits, defineExpose, onMounted, onUnmounted } from 'vue';
import { appEvents } from '@/store/app.events';
import autoscrollStore from '@/store/autoscroll.store';

const emits = defineEmits(['toggleScrolling', 'increaseSpeed', 'decreaseSpeed'])
const props = defineProps({
    hidden: {
        type: Boolean,
        default: false
    },
    showLabel: {
        type: Boolean,
        default: true
    },
    showKeyboardShortcuts: {
        type: Boolean,
        default: true
    },
    theme: {
        type: String,
        default: 'light',
        validator: (value) => {
            return ['light', 'dark'].includes(value);
        }
    }
})

const showScrollDropdown = ref(false)
const isScrolling = ref(false)
const scrollSpeed = ref(1)
const mapKeyPressed = ref({})

const toggleScrollDropdown = () => {
    showScrollDropdown.value = !showScrollDropdown.value
}

const selectSpeedByBar = (index) => {
    // Map bar index to speed: 0=1, 1=2, 2=3, 3=4, 4=5
    const speed = index + 1
    scrollSpeed.value = speed * 2
    autoscrollStore.state.scrollSpeed = scrollSpeed.value;
}
const getBarClass = (index) => {
    if (!isScrolling.value) {
        // Gray bars when stopped
        return 'bg-gray-300 dark:bg-gray-600'
    }
    
    // Calculate which bars should be colored based on speed
    const speedLevel = Math.ceil(scrollSpeed.value / 2) // 1-2, 3-4, 5-6, 7-8, 9-10
    const shouldColor = index < speedLevel
    
    if (!shouldColor) {
        return 'bg-gray-300 dark:bg-gray-600'
    }
    
    // Color bars based on speed level
    const colors = [
        'bg-green-400 dark:bg-green-500', // Slow (1-2)
        'bg-yellow-400 dark:bg-yellow-500', // Medium (3-4)
        'bg-orange-400 dark:bg-orange-500', // Fast (5-6)
        'bg-red-400 dark:bg-red-500', // Very Fast (7-8)
        'bg-purple-400 dark:bg-purple-500' // Maximum (9-10)
    ]
    
    return colors[index] || 'bg-gray-300 dark:bg-gray-600'
}

const handleKeydown = (event) => {
    // Track pressed keys
    mapKeyPressed.value[event.key] = true
    // Only handle keyboard shortcuts when not in an input field
    if (event.target.tagName === 'INPUT' || event.target.tagName === 'TEXTAREA' 
        || event.target.getAttribute('contenteditable') === 'true') {
        return
    }
    
    if (event.key === 'PageDown') {
        event.preventDefault()
        if (!isScrolling.value) {
            toggleScrolling()
        } else {
            increaseSpeed()
        }
    } else if (event.key === 'PageUp') {
        event.preventDefault()
        if (isScrolling.value) {
            if (scrollSpeed.value <= 1) {
                toggleScrolling()
            } else {
                decreaseSpeed()
            }
        }
    }
}

const handleKeyUp = (event) => {
    delete mapKeyPressed.value[event.key]
} 

const toggleScrolling = () => {
    isScrolling.value = !isScrolling.value
    autoscrollStore.state.scrollSpeed = scrollSpeed.value;
    emits('toggleScrolling', isScrolling.value)
    appEvents.emit('toggleAutoScroll', {isScrolling: isScrolling.value, speed: scrollSpeed.value})
}

const increaseSpeed = () => {
    if (scrollSpeed.value < 10) {
        scrollSpeed.value = Math.min(10, scrollSpeed.value + 0.5)
        emits('increaseSpeed', scrollSpeed.value)
        autoscrollStore.state.scrollSpeed = scrollSpeed.value;
    }
}

const decreaseSpeed = () => {
    if (scrollSpeed.value > 1) {
        scrollSpeed.value = Math.max(1, scrollSpeed.value - 0.5)
        emits('decreaseSpeed', scrollSpeed.value)
        autoscrollStore.state.scrollSpeed = scrollSpeed.value;
    }
}

onMounted(() => {
    document.addEventListener('keydown', handleKeydown)
    document.addEventListener('keyup', handleKeyUp);
    scrollSpeed.value = autoscrollStore.state.scrollSpeed;
    isScrolling.value = autoscrollStore.state.isScrolling;
})

onUnmounted(() => {
    document.removeEventListener('keydown', handleKeydown)
    document.removeEventListener('keyup', handleKeyUp)
})

autoscrollStore.watch((state) => state.isScrolling, (newVal) => {
    isScrolling.value = newVal
})
autoscrollStore.watch((state) => state.scrollSpeed, (newVal) => {
    scrollSpeed.value = newVal
})

defineExpose({
    toggleScrollDropdown
})
</script>